import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAccessToken, setAccessToken, hasSession } from "../../services/api.ts";
import API from "../../services/api.ts";
import NotificationDropdown from "../notifications/NotificationDropdown";

const TAB_STYLE = {
  background: "none",
  border: "none",
  color: "#d0d6e8",
  fontSize: "18px",
  fontFamily: "Arial, sans-serif",
  fontWeight: "500",
  padding: "10px 18px",
  cursor: "pointer",
  borderRadius: "6px",
  transition: "color 0.15s, background 0.15s",
  whiteSpace: "nowrap",
};

const NavTab = ({ children, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        ...TAB_STYLE,
        color: hovered ? "#ffffff" : "#d0d6e8",
        background: hovered ? "rgba(255,255,255,0.08)" : "none",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
};

const SECTION_LABEL = {
  fontSize: "10px", letterSpacing: "1.3px", fontWeight: "700",
  color: "rgba(255,255,255,0.5)", fontFamily: "Poppins, sans-serif",
  padding: "4px 10px 5px", textTransform: "uppercase",
};

const EMPTY_TEXT = {
  padding: "3px 10px 7px", color: "rgba(255,255,255,0.4)",
  fontSize: "12px", fontFamily: "Poppins, sans-serif",
};

const DIVIDER = { height: "1px", background: "rgba(255,255,255,0.08)", margin: "4px 8px" };

const DropdownItem = ({ icon, label, onClick, dim }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "9px",
        width: "100%",
        background: hovered ? "rgba(255,255,255,0.09)" : "none",
        border: "none",
        borderRadius: "6px",
        padding: "7px 10px",
        color: dim ? "rgba(255,255,255,0.52)" : (hovered ? "#ffffff" : "rgba(255,255,255,0.92)"),
        fontSize: dim ? "13px" : "15px",
        fontFamily: "Poppins, sans-serif",
        cursor: "pointer",
        textAlign: "left",
        transition: "background 0.12s, color 0.12s",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <i className={`fa ${icon}`} style={{ width: "13px", opacity: dim ? 0.55 : 0.8, flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    </button>
  );
};

const HomeSection = ({ title, viewAllPath, items, itemIcon, buildLabel, onNavigate, emptyText, onClose }) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <>
      <div style={SECTION_LABEL}>{title}</div>
      <DropdownItem icon="fa-th-list" label={`View all ${title.toLowerCase()}`} onClick={() => { onNavigate(viewAllPath); onClose(); }} />
      <button
        onClick={() => setExpanded(e => !e)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "9px",
          width: "100%",
          background: hovered ? "rgba(255,255,255,0.06)" : "none",
          border: "none",
          borderRadius: "6px",
          padding: "6px 10px",
          color: "rgba(255,255,255,0.6)",
          fontSize: "12.5px",
          fontFamily: "Poppins, sans-serif",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.12s",
        }}
      >
        <i className="fa fa-clock-o" style={{ width: "13px", opacity: 0.6, flexShrink: 0 }} />
        <span style={{ flex: 1 }}>Recent</span>
        <i
          className="fa fa-chevron-down"
          style={{ fontSize: "9px", opacity: 0.6, transition: "transform 0.15s", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {expanded && (
        items.length === 0
          ? <div style={EMPTY_TEXT}>{emptyText}</div>
          : items.map(x => (
            <div key={x.id} style={{ paddingLeft: "18px" }}>
              <DropdownItem icon={itemIcon} label={buildLabel(x)} onClick={() => { onNavigate(x); onClose(); }} />
            </div>
          ))
      )}
    </>
  );
};

const HomeDropdown = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentStudies, setRecentStudies] = useState([]);
  const [recentViewers, setRecentViewers] = useState([]);
  const [recentModels, setRecentModels] = useState([]);
  const [recentTools, setRecentTools] = useState([]);
  const [recentDatasets, setRecentDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = async () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
    setHovered(true);
    if (!loaded && (getAccessToken() || hasSession())) {
      setLoading(true);
      try {
        const parse = r => Array.isArray(r.data) ? r.data : (r.data?.results ?? []);
        // Best-effort per resource — a 404/500 on one doesn't break the rest.
        const settled = await Promise.allSettled([
          API.get("/projects/"),
          API.get("/studies/"),
          API.get("/viewers/"),
          API.get("/models/"),
          API.get("/datasets/"),
        ]);
        const [projRes, studyRes, viewerRes, modelRes, datasetRes] = settled;
        const pick = r => (r.status === "fulfilled" ? parse(r.value) : []);
        setRecentProjects(pick(projRes).slice(0, 4));
        setRecentStudies(pick(studyRes).slice(0, 4));
        setRecentViewers(pick(viewerRes).slice(0, 4));
        // Tools and Models share the /models/ endpoint — split by entity_type.
        const allModels = pick(modelRes);
        setRecentModels(allModels.filter(m => m.entity_type !== "tool").slice(0, 4));
        setRecentTools(allModels.filter(m => m.entity_type === "tool").slice(0, 4));
        setRecentDatasets(pick(datasetRes).slice(0, 4));
        setLoaded(true);
      } catch (e) {}
      setLoading(false);
    }
  };

  const handleLeave = () => {
    setHovered(false);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  return (
    <div style={{ position: "relative" }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => navigate("/Dashboard")}
        style={{
          ...TAB_STYLE,
          color: hovered ? "#ffffff" : "#d0d6e8",
          background: hovered ? "rgba(255,255,255,0.08)" : "none",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        My Home
        <i
          className="fa fa-chevron-down"
          style={{ fontSize: "9px", opacity: 0.5, transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          zIndex: 9999,
          background: "#0f1427",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "10px",
          padding: "10px 8px",
          minWidth: "270px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(56,182,255,0.07)",
        }}>
          {loading ? (
            <div style={{ padding: "14px 10px", color: "rgba(255,255,255,0.5)", fontSize: "13px", textAlign: "center", fontFamily: "Poppins, sans-serif" }}>
              Loading…
            </div>
          ) : (
            <>
              <HomeSection
                title="Projects"
                viewAllPath="/Projects"
                items={recentProjects}
                itemIcon="fa-folder-open"
                buildLabel={p => p.name || p.title || `Project ${p.id}`}
                onNavigate={x => navigate(typeof x === "string" ? x : `/Projects/${x.id}`)}
                emptyText="No projects yet"
                onClose={() => setOpen(false)}
              />

              <div style={DIVIDER} />

              <HomeSection
                title="Studies"
                viewAllPath="/Studies"
                items={recentStudies}
                itemIcon="fa-flask"
                buildLabel={s => s.name || s.title || `Study ${s.id}`}
                onNavigate={x => navigate(typeof x === "string" ? x : `/Study/${x.id}`)}
                emptyText="No studies yet"
                onClose={() => setOpen(false)}
              />

              <div style={DIVIDER} />

              <HomeSection
                title="Datasets"
                viewAllPath="/Datasets"
                items={recentDatasets}
                itemIcon="fa-database"
                buildLabel={d => d.name || d.title || `Dataset ${d.id}`}
                onNavigate={x => navigate(typeof x === "string" ? x : "/Datasets")}
                emptyText="No datasets yet"
                onClose={() => setOpen(false)}
              />

              <div style={DIVIDER} />

              <HomeSection
                title="Viewers"
                viewAllPath="/Viewers"
                items={recentViewers}
                itemIcon="fa-eye"
                buildLabel={v => v.name || v.title || `Viewer ${v.id}`}
                onNavigate={x => navigate(typeof x === "string" ? x : "/Viewers")}
                emptyText="No viewers yet"
                onClose={() => setOpen(false)}
              />

              <div style={DIVIDER} />

              <HomeSection
                title="Models"
                viewAllPath="/Models"
                items={recentModels}
                itemIcon="fa-brain"
                buildLabel={m => m.name || m.title || `Model ${m.id}`}
                onNavigate={x => navigate(typeof x === "string" ? x : "/Models")}
                emptyText="No models yet"
                onClose={() => setOpen(false)}
              />

              <div style={DIVIDER} />

              <HomeSection
                title="Tools"
                viewAllPath="/Tools"
                items={recentTools}
                itemIcon="fa-wrench"
                buildLabel={t => t.name || t.title || `Tool ${t.id}`}
                onNavigate={x => navigate(typeof x === "string" ? x : "/Tools")}
                emptyText="No tools yet"
                onClose={() => setOpen(false)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const UserDropdown = ({ onLogout, isStaff }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
    setHovered(true);
  };

  const handleLeave = () => {
    setHovered(false);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div style={{ position: "relative" }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        style={{
          background: hovered ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.1)",
          border: "none",
          borderRadius: "50%",
          width: "34px",
          height: "34px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#ffffff",
          fontSize: "16px",
          transition: "background 0.15s",
        }}
      >
        <i className="fa fa-user" />
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          right: 0,
          zIndex: 9999,
          background: "#0f1427",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "10px",
          padding: "6px",
          minWidth: "190px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.65)",
        }}>
          <DropdownItem icon="fa-user-circle" label="Profile" onClick={() => go("/Profile")} />
          <DropdownItem icon="fa-wrench" label="Dev Tools" onClick={() => go("/DevTools")} />
          {isStaff && (
            <DropdownItem icon="fa-history" label="Activity Log" onClick={() => go("/ActivityLog")} />
          )}
          <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "4px 4px" }} />
          <DropdownItem
            icon="fa-sign-out"
            label="Sign Out"
            onClick={() => { setOpen(false); onLogout(); }}
            dim
          />
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const [isStaff, setIsStaff] = useState(false);
  const isAuthenticated = !!getAccessToken() || hasSession();

  useEffect(() => {
    const token = getAccessToken();
    if (token || hasSession()) {
      API.get("/accounts/users/me/")
        .then(res => setIsStaff(!!res.data.is_staff))
        .catch(() => {});
    }
  }, []);

  const handleLogout = async () => {
    // Server-side logout: bumps session_version and blacklists the refresh
    // token so any intercepted access token (e.g. replayed via Burp) is
    // rejected immediately instead of remaining valid until natural expiry
    // (VUL-WEB-H01 — Improper Session Termination & Token Reuse).
    const refresh_token = localStorage.getItem("refresh_token");
    try {
      await API.post("/accounts/logout/", { refresh: refresh_token });
    } catch {
      // Best-effort — proceed with local cleanup even if the server is
      // unreachable or the token is already invalid.
    }

    setAccessToken(null);
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    toast.success("Successfully logged out.");
    navigate("/");
  };

  return (
    <nav style={{
      position: "relative",
      zIndex: 10,
      backgroundColor: "#0b0f1e",
      display: "flex",
      alignItems: "center",
      padding: "0 28px",
      height: "76px",
      borderBottom: "1px solid rgba(255,255,255,0.07)",
    }}>

      {/* Logo */}
      <img
        src="/assets/logo_no_background.png"
        alt="Logo"
        style={{ width: "52px", cursor: "pointer", marginRight: "20px", flexShrink: 0 }}
        onClick={() => navigate("/")}
      />

      {/* Left cluster: Notifications, then My Home */}
      {isAuthenticated && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ color: "#d0d6e8" }}>
            <NotificationDropdown />
          </div>
          <HomeDropdown />
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Right tabs — always visible */}
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        <NavTab onClick={() => navigate("/OpenModels")}>Medical Models</NavTab>
        <NavTab onClick={() => navigate("/OpenDatasets")}>Open Datasets</NavTab>
        <NavTab onClick={() => navigate("/OpenStudies")}>Research Studies</NavTab>
      </div>

      {/* Icons */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "16px" }}>
        {isAuthenticated ? (
          <UserDropdown onLogout={handleLogout} isStaff={isStaff} />
        ) : (
          <button
            onClick={() => navigate("/login")}
            style={{
              background: "transparent",
              border: "1px solid rgba(56,182,255,0.4)",
              borderRadius: "6px",
              padding: "6px 14px",
              color: "#ffffff",
              fontSize: "13px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background 0.15s, border-color 0.15s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(56,182,255,0.08)";
              e.currentTarget.style.borderColor = "rgba(56,182,255,0.6)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(56,182,255,0.4)";
            }}
          >
            Log in
          </button>
        )}

        {!isAuthenticated && (
          <button
            onClick={() => navigate("/register")}
            style={{
              background: "rgba(56,182,255,0.15)",
              border: "1px solid rgba(56,182,255,0.4)",
              borderRadius: "6px",
              padding: "6px 14px",
              color: "#38b6ff",
              fontSize: "13px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Register
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
